// components/BookingCalendar.js
import React from 'react';
import { ChevronLeft, ChevronRight, Calendar, Bed } from 'lucide-react';
import { format, subMonths, addMonths, getDaysInMonth, startOfMonth, differenceInDays } from 'date-fns';

const BookingCalendar = ({
  currentMonth,
  setCurrentMonth,
  selectedCheckIn,
  selectedCheckOut,
  hoveredDate,
  invalidDateMessage,
  monthDayStatuses,
  monthMinPrices,
  reservations,
  handleDateClick,
  setHoveredDate,
  getPossibleDurationsFromDate,
  language,
  t,
  dateLocale,
    loadingMonthPrices  // AJOUTER CETTE LIGNE

}) => {
  
  const weekDays = language === 'fr' 
    ? ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']
    : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  
  const renderCalendar = () => {
    const firstDay = startOfMonth(currentMonth);
    const daysInMonth = getDaysInMonth(currentMonth);
    const startPadding = firstDay.getDay() || 7;
    const days = [];

    // Ajouter les jours vides au début
    for (let i = 1; i < startPadding; i++) {
      days.push(<div key={`pad-${i}`} className="p-2"></div>);
    }

    // Ajouter les jours du mois
    for (let i = 1; i <= daysInMonth; i++) {
      // ✅ Construire la date manuellement pour éviter le glissement de fuseau
      const year = currentMonth.getFullYear();
      const month = String(currentMonth.getMonth() + 1).padStart(2, '0');
      const day = String(i).padStart(2, '0');
      
      // Cette chaîne est "immuable" face aux fuseaux horaires
      const dateKey = `${year}-${month}-${day}`;
      const date = new Date(dateKey + 'T00:00:00');
      
      // Vérifier si la date est passée
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const currentDate = new Date(date);
      currentDate.setHours(0, 0, 0, 0);
      const isPast = currentDate < today;
      
      const status = monthDayStatuses[dateKey];
      const minPrice = monthMinPrices[dateKey];

      // -2 = départ possible mais arrivée complète (case diagonale verte/rouge)
      const isDepartureOnlySplit = !isPast && minPrice === -2;
      // Un jour est disponible si minPrice n'est pas null/-2 ET pas dans le passé
      const isAvailable = !isPast && minPrice !== null && minPrice !== undefined && minPrice !== -2;

      const hasReservation = reservations.some(res => {
        const checkIn = res.checkIn?.toDate ? res.checkIn.toDate() : new Date(res.checkIn);
        const checkOut = res.checkOut?.toDate ? res.checkOut.toDate() : new Date(res.checkOut);
        return date >= checkIn && date < checkOut && res.status !== 'cancelled';
      });
      
      const isCheckIn = selectedCheckIn && format(selectedCheckIn, 'yyyy-MM-dd') === dateKey;
      const isCheckOut = selectedCheckOut && format(selectedCheckOut, 'yyyy-MM-dd') === dateKey;
      const isInRange = selectedCheckIn && selectedCheckOut && 
                       date > selectedCheckIn && date < selectedCheckOut;
      
      let isHovered = false;
      let isInvalidHover = false;
      
      if (selectedCheckIn && !selectedCheckOut && hoveredDate && date > selectedCheckIn && date <= hoveredDate) {
        const nights = differenceInDays(hoveredDate, selectedCheckIn);
        const possibleDurations = getPossibleDurationsFromDate(selectedCheckIn);
        
        if (possibleDurations.includes(nights)) {
          isHovered = true;
        } else {
          isInvalidHover = true;
        }
      }

      const showErrorTooltip = invalidDateMessage && 
                               format(invalidDateMessage.date, 'yyyy-MM-dd') === dateKey;

      days.push(
        <div
          key={i}
          onClick={() => !isPast && handleDateClick(date)}
          onMouseEnter={() => {
            if (!isPast && selectedCheckIn && !selectedCheckOut && date > selectedCheckIn) {
              setHoveredDate(date);
            }
          }}
          onMouseLeave={() => setHoveredDate(null)}
          className={`
            relative p-2 sm:p-3 text-center border transition-all flex flex-col items-center justify-center min-h-[48px] sm:min-h-[60px]
            ${isPast ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'cursor-pointer'}
            ${!isPast && !isAvailable && !isDepartureOnlySplit && !isCheckIn && !isCheckOut && !isInRange ? 'bg-red-100 text-red-400 cursor-not-allowed' : ''}
            ${isAvailable && !isCheckIn && !isCheckOut && !isInRange && !isHovered && !isInvalidHover ?
              'bg-green-50 text-black border-green-200 hover:bg-green-100' : ''}
            ${isCheckIn || isCheckOut ? 'bg-blue-500 text-white border-blue-600' : ''}
            ${isInRange ? 'bg-blue-100 text-black border-blue-200' : ''}
            ${isHovered ? 'bg-blue-200 text-black border-blue-300' : ''}
            ${isInvalidHover ? 'bg-red-200 text-red-800 border-red-300' : ''}
          `}
          style={isDepartureOnlySplit && !isCheckIn && !isCheckOut && !isInRange && !isHovered && !isInvalidHover ? {
            background: 'linear-gradient(135deg, #f0fdf4 50%, #fee2e2 50%)',
            borderColor: '#bbf7d0'
          } : undefined}
        >
          <div className="font-medium">{i}</div>
          
          {!isPast && isAvailable && minPrice > 0 && (
            <div className="text-xs mt-1 hidden sm:block">
              <span className={isCheckIn || isCheckOut ? 'text-white font-semibold' : 'text-green-700'}>
                {minPrice}€
              </span>
            </div>
          )}
          
          {status && status.isOpen && !status.isBlocked && (
            <div className="absolute top-0 right-0 text-[9px] px-1">
              {status.canArrive && status.canDepart && (
                <>
                  <span className="text-blue-500">↓</span>
                  <span className="text-orange-500">→</span>
                </>
              )}
              {status.canArrive && !status.canDepart && <span className="text-blue-500">↓</span>}
              {status.canDepart && !status.canArrive && <span className="text-orange-500">→</span>}
            </div>
          )}
          
          {showErrorTooltip && (
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-50">
              <div className="bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                {invalidDateMessage.message}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                  <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    return days;
  };

  return (
    <div>
      <h2 className="text-2xl font-light mb-6 text-center">
        {t.selectDates}
      </h2>
      
      {selectedCheckIn && !selectedCheckOut && (
        <div className="mb-4 p-4 bg-gray-50 border border-black rounded-lg">
          <div className="flex items-start gap-2">
            <Calendar className="w-5 h-5 text-black mt-0.5" />
            <div>
              <p className="text-sm font-medium text-black">
                {t.arrivalOn} {format(selectedCheckIn, language === 'fr' ? 'EEEE d MMMM' : 'EEEE, MMMM d', { locale: dateLocale })}
              </p>
              <p className="text-sm text-black mt-1">
                {(() => {
                  const durations = getPossibleDurationsFromDate(selectedCheckIn);
                  if (durations.length === 0) {
                    return t.noDurationAvailable;
                  } else if (durations.length === 1) {
                    return `${t.onlyStay} ${durations[0]} ${durations[0] > 1 ? t.nights : t.night}`;
                  } else {
                    const minDuration = Math.min(...durations);
                    const maxDuration = Math.max(...durations);
                    return language === 'fr' 
                      ? `Séjour de ${minDuration} à ${maxDuration} nuits`
                      : `Stay from ${minDuration} to ${maxDuration} nights`;
                  }
                })()}
              </p>
            </div>
          </div>
        </div>
      )}
      

<div className="mb-6 flex justify-between items-center">
        <button 
          onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
          className="p-2 border border-black hover:bg-black hover:text-white transition"
        >
          <ChevronLeft size={20} />
        </button>
        <div className="text-center">
          <h3 className="text-xl">
            {format(currentMonth, language === 'fr' ? 'MMMM yyyy' : 'MMMM yyyy', { locale: dateLocale })}
          </h3>
          {loadingMonthPrices && (
            <div className="text-sm text-gray-500 mt-1">
              {language === 'fr' ? 'Chargement des dispos et des prix...' : 'Loading...'}
            </div>
          )}
        </div>



        <button 
          onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
          className="p-2 border border-black hover:bg-black hover:text-white transition"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-6">
        {weekDays.map(day => (
          <div key={day} className="p-2 text-center text-sm font-medium uppercase">
            {day}
          </div>
        ))}
        {renderCalendar()}
      </div>

      <div className="flex items-center justify-center space-x-6 text-sm mb-6">
        <div className="flex items-center">
          <div className="w-4 h-4 bg-green-50 border border-green-200 mr-2"></div>
          <span>{t.available}</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 bg-blue-500 mr-2"></div>
          <span>{t.selected}</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 bg-red-100 mr-2"></div>
          <span>{t.fullOrClosed}</span>
        </div>
      </div>
    </div>
  );
};

export default BookingCalendar;